import { AlertCircle } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card.js";
import { Field } from "../ui/field.js";
import { Input } from "../ui/input.js";

export interface SignUpFormProps {
  onSuccess?: (user: { id: string; email: string }) => void;
  signInSlot?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  /** Server enforces ≥8 chars; this is the client-side hint. */
  minPasswordLength?: number;
}

export const SignUpForm = ({
  onSuccess,
  signInSlot,
  title = "Create your account",
  description = "Just a few details to get started.",
  minPasswordLength = 8,
}: SignUpFormProps) => {
  const client = useUmClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < minPasswordLength;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (tooShort) return;
    setLoading(true);
    setError(null);
    const res = await client.signUp({
      name: name.trim(),
      email: email.trim(),
      password,
    });
    setLoading(false);
    if (res.ok) {
      onSuccess?.({ id: res.data.user.id, email: res.data.user.email });
    } else {
      setError(errorMessage(res.error, "Sign-up failed. Please try again."));
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1.5">
        <CardTitle className="text-2xl">{title}</CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4" noValidate>
          <Field label="Name" htmlFor="name" required>
            <Input
              id="name"
              autoComplete="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ada Lovelace"
            />
          </Field>
          <Field label="Email" htmlFor="email" required>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </Field>
          <Field
            label="Password"
            htmlFor="password"
            required
            description={`At least ${minPasswordLength} characters.`}
            error={tooShort ? `Password must be at least ${minPasswordLength} characters.` : undefined}
          >
            <Input
              id="password"
              type="password"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={tooShort || undefined}
            />
          </Field>
          {error ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <Button type="submit" className="w-full" loading={loading} disabled={tooShort}>
            {loading ? "Creating account…" : "Create account"}
          </Button>
          {signInSlot ? (
            <p className="text-center text-sm text-muted-foreground">{signInSlot}</p>
          ) : null}
        </form>
      </CardContent>
    </Card>
  );
};
